import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollTo } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";

export const NAV_LINKS = [
    { label: "Home", target: "#home", testid: "nav-item-home" },
    { label: "Chi siamo", target: "#chi-siamo", testid: "nav-item-chi-siamo" },
    { label: "Metodo", target: "#metodo", testid: "nav-item-metodo" },
    { label: "Aree di competenza", target: "#aree-competenza", testid: "nav-item-aree-competenza" },
    { label: "Team", target: "#team", testid: "nav-item-team" },
    { label: "Contatti", target: "#contatti", testid: "nav-item-contatti" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (target) => {
        setOpen(false);
        scrollTo(target);
    };

    const light = !scrolled && !open;

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
                    scrolled
                        ? "border-b border-[#1A1A1A]/10 bg-[#FAF7F2]/85 backdrop-blur-md"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
                    <button
                        data-testid="nav-logo"
                        onClick={() => go("#home")}
                        className="flex items-center gap-3"
                        aria-label="GPS srls - Home"
                    >
                        <img src="/assets/logo-pink.png" alt="GPS srls" className="h-12 w-auto object-contain" />
                        <span
                            className={`hidden font-serif text-lg tracking-wide transition-colors duration-500 sm:block ${
                                light ? "text-[#FAF7F2]" : "text-[#1A1A1A]"
                            }`}
                        >
                            GPS <span className="italic text-[#B88282]">srls</span>
                        </span>
                    </button>

                    <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigazione principale">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.target}
                                data-testid={l.testid}
                                onClick={() => go(l.target)}
                                className={`text-sm tracking-wide transition-colors duration-300 hover:text-[#B88282] ${
                                    light ? "text-[#FAF7F2]/90" : "text-[#1A1A1A]"
                                }`}
                            >
                                {l.label}
                            </button>
                        ))}
                        <button
                            data-testid="header-cta-button"
                            onClick={() => go("#contatti")}
                            className={`flex items-center gap-1.5 rounded-full px-6 py-2.5 text-sm font-medium tracking-wide transition-all duration-300 ${
                                light
                                    ? "bg-[#FAF7F2] text-[#1A1A1A] hover:bg-[#D4A5A5]"
                                    : "bg-[#1A1A1A] text-white hover:bg-[#D4A5A5] hover:text-[#1A1A1A]"
                            }`}
                        >
                            Contatti
                            <ArrowUpRight className="h-4 w-4" />
                        </button>
                    </nav>

                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(true)}
                        aria-label="Apri menu"
                        className={`lg:hidden ${light ? "text-[#FAF7F2]" : "text-[#1A1A1A]"}`}
                    >
                        <Menu className="h-7 w-7" />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[60] flex flex-col bg-[#1A1A1A] px-6 py-6"
                    >
                        <div className="flex items-center justify-between">
                            <img src="/assets/logo-pink.png" alt="GPS srls" className="h-12 w-auto object-contain" />
                            <button
                                data-testid="mobile-menu-close-button"
                                onClick={() => setOpen(false)}
                                aria-label="Chiudi menu"
                                className="text-[#FAF7F2]"
                            >
                                <X className="h-8 w-8" />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2" aria-label="Menu mobile">
                            {NAV_LINKS.map((l, i) => (
                                <div key={l.target} className="overflow-hidden">
                                    <motion.button
                                        initial={{ y: "110%" }}
                                        animate={{ y: "0%" }}
                                        exit={{ y: "110%" }}
                                        transition={{ duration: 0.6, delay: 0.06 * i, ease: EASE }}
                                        data-testid={`mobile-${l.testid}`}
                                        onClick={() => go(l.target)}
                                        className="block py-2 text-left font-serif text-4xl text-[#FAF7F2] transition-colors hover:text-[#D4A5A5]"
                                    >
                                        <span className="mr-4 font-sans text-xs tracking-[0.3em] text-[#B88282]">
                                            0{i + 1}
                                        </span>
                                        {l.label}
                                    </motion.button>
                                </div>
                            ))}
                        </nav>
                        <p className="text-sm tracking-wide text-[#FAF7F2]/50">info@gpsrls.com · Mirano (VE)</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
